"use client";

import { FormEntry } from "@/component-library/components/user-input/forms/form-entry/FormEntry";
import { FC, ReactNode } from "react";
import {
    inquiryFormDateFieldContainerStyle,
    inquiryFormDateFieldInputEmptyStyle,
    inquiryFormDateFieldInputStyle,
    inquiryFormDatePlaceholderStyle,
} from "./PrivateOfficeInquiryForm.css";

interface DateInputProps {
    /** "date" for a day, "datetime-local" for a day and a time. */
    type: "date" | "datetime-local";
    value: string;
    onChange: (value: string) => void;
    /** Earliest selectable value, in the same format as `value`. */
    min?: string;
    required?: boolean;
    /**
     * Shown in place of the value while there is none. Only worth passing on a
     * field that is allowed to be empty -- iOS draws nothing at all in a date
     * control with no value, so without it the field looks broken rather than
     * blank.
     */
    emptyHint?: string;
    /** Names the control when no label of its own is wrapped around it. */
    "aria-label"?: string;
}

interface DateFieldProps extends DateInputProps {
    label: ReactNode;
}

/**
 * The date control and its box, without a label: for a field that puts it
 * next to another control under one shared label.
 */
export const DateInput: FC<DateInputProps> = ({
    type,
    value,
    onChange,
    min,
    required,
    emptyHint,
    "aria-label": ariaLabel,
}) => {
    const showsHint = !!emptyHint && !value;

    return (
        <div className={inquiryFormDateFieldContainerStyle}>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                min={min}
                required={required}
                aria-label={ariaLabel}
                className={`${inquiryFormDateFieldInputStyle} ${
                    showsHint ? inquiryFormDateFieldInputEmptyStyle : ""
                }`}
            />
            {/* Hidden from assistive tech: the label already says what the
                field is, so announcing the hint as well would repeat it. */}
            {showsHint && (
                <span
                    aria-hidden="true"
                    className={inquiryFormDatePlaceholderStyle}
                >
                    {emptyHint}
                </span>
            )}
        </div>
    );
};

/**
 * A date or date-and-time field, shaped like the other fields in the inquiry
 * forms rather than like a bare date control.
 *
 * Every date field in both forms goes through here or `DateInput`, which is
 * the point: the height of a date control is engine-specific, so the way to
 * have them all agree is to have them all built the same way. See
 * `inquiryFormDateFieldContainerStyle` for what that costs and why.
 */
export const DateField: FC<DateFieldProps> = ({ label, ...inputProps }) => (
    <FormEntry label={label}>
        <DateInput {...inputProps} />
    </FormEntry>
);
